import { gql, useMutation, useQuery } from '@apollo/client';
import {
	Box,
	Button,
	Flex,
	IconButton,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Select,
	SimpleGrid,
	Spinner,
	Text,
	WrapItem,
	useToast,
} from '@chakra-ui/react';
import {
	faChevronLeft,
	faChevronRight,
	faDownload,
	faEye,
	faFileAlt,
	faPen,
	faPrint,
	faRedo,
	faSave,
	faSearchMinus,
	faSearchPlus,
	faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import html2canvas from 'html2canvas';
import Lottie from 'lottie-react';
import { PDFDocument, degrees } from 'pdf-lib';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';
import AnimationFile from '../../assets/lotties/68784-transfer-cloud.json';
import c from '../../constant';
import LetterDateButtom from './LetterDateButtom';
import LetterDateTop from './LetterDateTop';

const UPLOAD_SIGNED_PDF_MUTATION = gql`
	mutation uploadSignedPdfTempFile($type: String!, $fileSize: Float!, $file: Upload!) {
		userUploadTempFile(type: $type, fileSize: $fileSize, file: $file) {
			id
		}
	}
`;

const GET_DG_SIGN_DATA = gql`
	query getDGSignData {
		getDGSignData
	}
`;

// const LETTERHEAD_HEAD_NO_NUMBER = 'https://object.irc.gov.kh/irc-public/public-service/background/Letter-Head-Insurant-top.png';
const LETTERHEAD_HEAD = 'https://object.irc.gov.kh/irc-public/public-service/background/letter-head-number-no-bg.png';
const LETTERHEAD_FOOT = 'https://object.irc.gov.kh/irc-public/public-service/background/Letter-Head-Insurant-bottom.png';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// ─── IndexedDB file cache (1-day TTL) ────────────────────────────────────────
const IDB_NAME = 'pdf-signer-cache';
const IDB_STORE = 'files';
const IDB_TTL = 24 * 60 * 60 * 1000;

function openIDB() {
	return new Promise((resolve, reject) => {
		const req = indexedDB.open(IDB_NAME, 1);
		req.onupgradeneeded = (e) => e.target.result.createObjectStore(IDB_STORE, { keyPath: 'url' });
		req.onsuccess = (e) => resolve(e.target.result);
		req.onerror = () => reject(req.error);
	});
}

// Strip query string so S3 presigned URLs cache by path, not by expiry token
function cacheKey(url) {
	try {
		return new URL(url).origin + new URL(url).pathname;
	} catch {
		return url;
	}
}

async function getCachedBlob(url) {
	try {
		const db = await openIDB();
		const key = cacheKey(url);
		return new Promise((resolve) => {
			const req = db.transaction(IDB_STORE, 'readonly').objectStore(IDB_STORE).get(key);
			req.onsuccess = () => {
				const row = req.result;
				if (row && Date.now() - row.cachedAt < IDB_TTL) {
					resolve(URL.createObjectURL(new Blob([row.data], { type: row.mimeType })));
				} else resolve(null);
			};
			req.onerror = () => resolve(null);
		});
	} catch {
		return null;
	}
}

async function setCachedBlob(url, blob) {
	try {
		const key = cacheKey(url);
		const data = await blob.arrayBuffer();
		const db = await openIDB();
		const tx = db.transaction(IDB_STORE, 'readwrite');
		tx.objectStore(IDB_STORE).put({ url: key, data, mimeType: blob.type, cachedAt: Date.now() });
	} catch {
		/* non-fatal */
	}
}

// Download url with progress callback → { blobUrl, mimeType }
function fetchWithProgress(url, onProgress) {
	return new Promise((resolve, reject) => {
		const xhr = new XMLHttpRequest();
		xhr.open('GET', url);
		xhr.responseType = 'blob';
		xhr.onprogress = (e) => {
			if (e.lengthComputable) {
				onProgress({ loaded: e.loaded, total: e.total, percent: Math.floor((e.loaded / e.total) * 100) });
			} else {
				onProgress({ loaded: e.loaded, total: 0, percent: null });
			}
		};
		xhr.onload = () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				resolve({ blob: xhr.response, mimeType: xhr.response.type || 'application/octet-stream' });
			} else reject(new Error(`HTTP ${xhr.status}`));
		};
		xhr.onerror = () => reject(new Error('Network error'));
		xhr.send();
	});
}

// const SAMPLE_PDF_URL = "https://object.irc.gov.kh/irc-public/upload/2022/333ff7d0-c6cb-4545-9b22-17172936925b.JPEG";
const SAMPLE_PDF_URL = 'https://object.irc.gov.kh/irc-public/upload/2022/cf489960-810f-4eca-a54e-fd2c8417798e.pdf';

// html*: optional companion text block, inserted as its own independently
// draggable/resizable sticker (not attached to the image). htmlX/htmlY/htmlW
// are the *initial* position/size only, given as % of the page width/height —
// once inserted the user can freely move/resize it like any other sticker.
// `url` is intentionally omitted here — it's fetched at runtime from the
// getDGSignData query (presigned, short-lived) and merged in by id.
const SIGNATURE_META = [
	{
		id: 1,
		label: 'Signature, stamp with date at the top',
		width: '39.5%',
		HtmlComponent: LetterDateTop,
		previewUrl: 'https://object.irc.gov.kh/irc-public/public-service/background/stamp-sign-name-prev.png',
		htmlX: '31.1%',
		htmlY: '18.0%',
		htmlW: '60%',
	},
	{
		id: 2,
		label: 'Signature with date and title',
		width: '35.5%',
		HtmlComponent: LetterDateButtom,
		previewUrl: 'https://object.irc.gov.kh/irc-public/public-service/background/sign-name-prev.png',
		htmlX: '40%',
		htmlY: '70%',
		htmlW: '52%',
	},
	{
		id: 3,
		label: 'Signature with stamp',
		width: '39.5%',
		previewUrl: 'https://object.irc.gov.kh/irc-public/public-service/background/stamp-sign-name-prev.png',
	},
	{
		id: 4,
		label: 'Signature',
		width: '35.5%',
		previewUrl: 'https://object.irc.gov.kh/irc-public/public-service/background/sign-name-prev.png',
	},
	{
		id: 5,
		label: 'Signature, No Name for English use',
		width: '35.5%',
		previewUrl: 'https://object.irc.gov.kh/irc-public/public-service/background/sign-no-name-prev.png',
	},
];
const BASE_W = 1400; // canvas resolution — higher = sharper at all zoom levels
const ZOOM_STEP = 0.15;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 3.0;
const INITIAL_W = 160;
const MIN_W = 40;
const PAGE_BORDER = '2px solid rgba(52,152,219,0.5)';

function getClientXY(e) {
	if (e.touches && e.touches.length > 0) {
		return { x: e.touches[0].clientX, y: e.touches[0].clientY };
	}
	return { x: e.clientX, y: e.clientY };
}

function loadImageSize(src) {
	return new Promise((resolve) => {
		const img = new Image();
		img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
		img.onerror = () => resolve({ w: 160, h: 80 });
		img.src = src;
	});
}

const IMAGE_EXTS = /\.(jpe?g|png|gif|webp|bmp|tiff?)(\?.*)?$/i;

function isImageUrl(url) {
	if (!url) return false;
	try {
		const path = new URL(url).pathname;
		return IMAGE_EXTS.test(path);
	} catch {
		return IMAGE_EXTS.test(url);
	}
}

// Fetch an image URL and wrap it in a single-page PDF blob.
// The PDF page is sized to the image's natural dimensions (in pts, 1pt = 1px here).

// Kicks off loading the custom Khmer webfonts as soon as this module runs (once,
// at import time) instead of on every signature pick — by the time a user
// actually clicks a stamp, document.fonts.ready has almost certainly already
// resolved, so rasterizeHtmlToImage doesn't pay that wait on the hot path.
const khmerFontsReady =
	typeof document !== 'undefined' && document.fonts
		? Promise.all([document.fonts.load('28px KhmerOSSiemreap'), document.fonts.load('28px KhmerOSMuolLight')])
				.then(() => document.fonts.ready)
				.catch(() => null)
		: Promise.resolve(null);

// Renders a React element off-screen, waits for webfonts to load, rasterizes it
// via html2canvas, and returns a PNG data URL + its natural aspect ratio (w/h).
// Used to turn LetterDateTop/LetterDateButtom into a plain flattened image once,
// at insertion time — from then on it's dragged/resized/stamped exactly like a
// signature image (no live text reflow to keep in sync with the stamped PDF).
async function rasterizeHtmlToImage(element, pxWidth = 800) {
	const host = document.createElement('div');
	host.style.position = 'fixed';
	host.style.top = '-9999px';
	host.style.left = '-9999px';
	host.style.width = `${pxWidth}px`;
	host.style.background = 'transparent';
	document.body.appendChild(host);

	try {
		ReactDOM.render(element, host);
		// Let the DOM paint before measuring/capturing.
		await new Promise((r) => setTimeout(r, 0));
		await khmerFontsReady;

		const canvas = await html2canvas(host, { backgroundColor: null, scale: 2 });
		const url = canvas.toDataURL('image/png');
		const ratio = canvas.width / canvas.height;
		return { url, ratio };
	} finally {
		ReactDOM.unmountComponentAtNode(host);
		document.body.removeChild(host);
	}
}

// ─── SigSticker ──────────────────────────────────────────────────────────────
function SigSticker({ sig, src, isActive, overlayRef, onChange, onRemove, onActivate, zoom, pageH, allowRotate = true, zIndex = 1 }) {
	const gestureRef = useRef(null);
	const HANDLE = 22;

	const getOverlayRect = () => overlayRef.current?.getBoundingClientRect();

	const clamp = (patch, current) => {
		const merged = { ...current, ...patch };
		const w = merged.w;
		const h = w / merged.ratio;
		const x = Math.max(0, Math.min(BASE_W - w, merged.x));
		const y = Math.max(0, Math.min(pageH - h, merged.y));
		return { ...patch, x, y };
	};

	const onBodyDown = (e) => {
		if (e.target !== e.currentTarget && e.target.dataset.handle) return;
		e.stopPropagation();
		e.preventDefault();
		onActivate();
		const { x, y } = getClientXY(e);
		gestureRef.current = { type: 'move', px: x, py: y, sx: sig.x, sy: sig.y };
		bind();
	};

	const onResizeDown = (e) => {
		e.stopPropagation();
		e.preventDefault();
		const { x } = getClientXY(e);
		gestureRef.current = { type: 'resize', px: x, sw: sig.w };
		bind();
	};

	const onRotateDown = (e) => {
		e.stopPropagation();
		e.preventDefault();
		const rect = getOverlayRect();
		if (!rect) return;
		const sw = sig.w * zoom;
		const sh = (sig.w / sig.ratio) * zoom;
		const cx = rect.left + sig.x * zoom + sw / 2;
		const cy = rect.top + sig.y * zoom + sh / 2;
		const { x, y } = getClientXY(e);
		gestureRef.current = {
			type: 'rotate',
			cx,
			cy,
			startAngle: Math.atan2(y - cy, x - cx) * (180 / Math.PI),
			startRot: sig.rotation,
		};
		bind();
	};

	const onMove = useCallback(
		(e) => {
			const g = gestureRef.current;
			if (!g) return;
			const { x, y } = getClientXY(e);
			if (g.type === 'move') {
				const rawPatch = { x: g.sx + (x - g.px) / zoom, y: g.sy + (y - g.py) / zoom };
				onChange(clamp(rawPatch, sig));
			} else if (g.type === 'resize') {
				const newW = Math.max(MIN_W, g.sw + (x - g.px) / zoom);
				onChange({ w: newW });
			} else if (g.type === 'rotate') {
				const angle = Math.atan2(y - g.cy, x - g.cx) * (180 / Math.PI);
				onChange({ rotation: g.startRot + (angle - g.startAngle) });
			}
		},
		[onChange, zoom, sig],
	);

	const onUp = useCallback(() => {
		gestureRef.current = null;
		unbind();
	}, []);

	function bind() {
		window.addEventListener('mousemove', onMove, { passive: false });
		window.addEventListener('mouseup', onUp);
		window.addEventListener('touchmove', onMove, { passive: false });
		window.addEventListener('touchend', onUp);
	}
	function unbind() {
		window.removeEventListener('mousemove', onMove);
		window.removeEventListener('mouseup', onUp);
		window.removeEventListener('touchmove', onMove);
		window.removeEventListener('touchend', onUp);
	}
	useEffect(() => () => unbind(), []);

	const w = sig.w;
	const h = sig.w / sig.ratio;

	return (
		<Box
			data-sticker-id={sig.id}
			position="absolute"
			left={`${sig.x}px`}
			top={`${sig.y}px`}
			width={`${w}px`}
			height={`${h}px`}
			zIndex={zIndex}
			style={{ transform: `rotate(${sig.rotation}deg)`, transformOrigin: 'center center' }}
			onMouseDown={onBodyDown}
			onTouchStart={onBodyDown}
			cursor="grab"
			userSelect="none"
			// prevent page scroll only while touching the sticker
			style={{ transform: `rotate(${sig.rotation}deg)`, transformOrigin: 'center center', touchAction: 'none' }}
		>
			<img
				src={src}
				alt="signature"
				draggable={false}
				style={{ width: '100%', height: '100%', objectFit: 'fill', pointerEvents: 'none', display: 'block' }}
			/>

			{isActive && <Box position="absolute" inset="-2px" border="2px dashed" borderColor="blue.400" borderRadius="md" pointerEvents="none" />}

			{isActive && (
				<>
					{/* Delete — top-left */}
					<Box
						data-handle="delete"
						position="absolute"
						top={`${-HANDLE / zoom / 2}px`}
						left={`${-HANDLE / zoom / 2}px`}
						w={`${HANDLE / zoom}px`}
						h={`${HANDLE / zoom}px`}
						bg="red.500"
						color="white"
						borderRadius="full"
						display="flex"
						alignItems="center"
						justifyContent="center"
						fontSize={`${14 / zoom}px`}
						fontWeight="bold"
						cursor="pointer"
						zIndex={10}
						boxShadow="md"
						onMouseDown={(e) => {
							e.stopPropagation();
							onRemove();
						}}
						onTouchStart={(e) => {
							e.stopPropagation();
							e.preventDefault();
							onRemove();
						}}
						_hover={{ bg: 'red.700' }}
					>
						×
					</Box>

					{/* Rotate — top-center */}
					{allowRotate && (
						<Box
							data-handle="rotate"
							position="absolute"
							top={`${(-HANDLE - 10) / zoom}px`}
							left={`calc(50% - ${HANDLE / zoom / 2}px)`}
							w={`${HANDLE / zoom}px`}
							h={`${HANDLE / zoom}px`}
							bg="purple.500"
							color="white"
							borderRadius="full"
							display="flex"
							alignItems="center"
							justifyContent="center"
							fontSize={`${13 / zoom}px`}
							cursor="grab"
							zIndex={10}
							boxShadow="md"
							onMouseDown={onRotateDown}
							onTouchStart={onRotateDown}
						>
							↻
						</Box>
					)}

					{/* Resize — bottom-right */}
					<Box
						data-handle="resize"
						position="absolute"
						bottom={`${-HANDLE / zoom / 2}px`}
						right={`${-HANDLE / zoom / 2}px`}
						w={`${HANDLE / zoom}px`}
						h={`${HANDLE / zoom}px`}
						bg="blue.500"
						color="white"
						borderRadius="full"
						display="flex"
						alignItems="center"
						justifyContent="center"
						fontSize={`${11 / zoom}px`}
						cursor="se-resize"
						zIndex={10}
						boxShadow="md"
						onMouseDown={onResizeDown}
						onTouchStart={onResizeDown}
					>
						⤡
					</Box>
				</>
			)}
		</Box>
	);
}

// ─── SimpleDatePicker — Day/Month/Year selects, styled like utils/DateTimeFormField
// but standalone (no Formik) and no time, since it's only used to pick a stamp date.
const DP_MONTHS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
function SimpleDatePicker({ value, onChange }) {
	const currentYear = new Date().getFullYear();
	const [day, setDay] = useState(value?.getDate() || new Date().getDate());
	const [month, setMonth] = useState(value ? value.getMonth() : new Date().getMonth());
	const [year, setYear] = useState(value?.getFullYear() || currentYear);

	const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();

	useEffect(() => {
		onChange(new Date(year, month, day));
	}, [day, month, year]);

	const selectProps = { style: { borderRadius: '3px' }, size: 'sm' };

	return (
		<SimpleGrid columns={3} spacing={3}>
			<WrapItem>
				<Select value={day} onChange={(e) => setDay(parseInt(e.target.value))} {...selectProps}>
					{Array.from({ length: daysInMonth(year, month) }).map((_, i) => (
						<option key={i + 1} value={i + 1}>
							{i + 1 < 10 ? `0${i + 1}` : i + 1}
						</option>
					))}
				</Select>
			</WrapItem>
			<WrapItem>
				<Select value={month} onChange={(e) => setMonth(parseInt(e.target.value))} {...selectProps}>
					{DP_MONTHS.map((m, i) => (
						<option key={m} value={i}>
							{m}
						</option>
					))}
				</Select>
			</WrapItem>
			<WrapItem>
				<Select value={year} onChange={(e) => setYear(parseInt(e.target.value))} {...selectProps}>
					{[currentYear - 1, currentYear, currentYear + 1].map((yr) => (
						<option key={yr} value={yr}>
							{yr}
						</option>
					))}
				</Select>
			</WrapItem>
		</SimpleGrid>
	);
}

// ─── SignaturePickerModal ────────────────────────────────────────────────────
// Isolated + memoized so it doesn't re-render (and re-run the Khmer/lunar date
// formatting in every HtmlComponent preview) on every drag/zoom/etc. state
// change elsewhere in PDFSigner — only when its own props actually change.
const SignaturePickerModal = React.memo(function SignaturePickerModal({
	isOpen,
	onClose,
	pickerDate,
	onDateChange,
	onSelect,
	inserting,
	t,
	signatures,
	showLetterheadOption = false,
	withLetterhead,
	onWithLetterheadChange,
	showPreview = false,
}) {
	return (
		<Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
			<ModalOverlay />
			<ModalContent borderRadius={c.borderRadius}>
				<ModalHeader></ModalHeader>
				<ModalCloseButton />
				<ModalBody pb={6} pt={10} position="relative">
					<Box mb={4}>
						<Text fontSize="sm" fontWeight="600" color="gray.700" mb={1}>
							{t('Date')}
						</Text>
						<SimpleDatePicker value={pickerDate} onChange={onDateChange} />
					</Box>
					{showLetterheadOption && (
						<Box mb={4}>
							<label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', width: 'fit-content' }}>
								<input type="checkbox" checked={withLetterhead} onChange={(e) => onWithLetterheadChange(e.target.checked)} />
								<Text fontSize="sm" fontWeight="600" color="gray.700">
									{t('Letterhead')}
								</Text>
							</label>
						</Box>
					)}
					<SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} opacity={inserting ? 0.4 : 1} pointerEvents={inserting ? 'none' : 'auto'}>
						{signatures.map((signature) => (
							<Box
								key={signature.id}
								borderWidth="1px"
								borderColor="gray.200"
								borderRadius="md"
								p={4}
								cursor="pointer"
								textAlign="center"
								_hover={{ borderColor: 'blue.400', bg: 'blue.50' }}
								onClick={() => onSelect(signature, signature.HtmlComponent ? pickerDate : null)}
							>
								{signature.HtmlComponent && (
									<Box
										mb={2}
										p={2}
										borderWidth="1px"
										borderColor="gray.100"
										borderRadius="md"
										bg="gray.50"
										sx={{
											'p, i': { margin: 0, padding: 0, color: 'black', whiteSpace: 'nowrap' },
										}}
									>
										<signature.HtmlComponent date={pickerDate} fontSize={11} />
									</Box>
								)}
								<Flex align="center" justify="center" minH="100px">
									<img
										src={showPreview ? signature.previewUrl : signature.url}
										alt={signature.label}
										style={{ maxWidth: '100%', maxHeight: '100px', objectFit: 'contain' }}
									/>
								</Flex>
							</Box>
						))}
					</SimpleGrid>
					{inserting && (
						<Flex position="absolute" inset={0} align="center" justify="center" direction="column" gap={2} bg="whiteAlpha.700" borderRadius="md">
							<Spinner size="lg" color="blue.500" thickness="3px" />
							<Text fontSize="sm" color="gray.700" fontWeight="600">
								{t('Applying stamp...')}
							</Text>
						</Flex>
					)}
				</ModalBody>
			</ModalContent>
		</Modal>
	);
});

// ─── shared icon style — guarantees perfect centering inside IconButton ──────
const IC = { display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 };

// Toolbar IconButton that never changes opacity when disabled — prevents layout shift
const TB = React.forwardRef((props, ref) => (
	<IconButton ref={ref} variant="ghost" size="sm" borderRadius="full" sx={{ _disabled: { opacity: 1, cursor: 'default' } }} {...props} />
));

function NavBar({ children, borderBottom = true }) {
	return (
		<Flex
			align="center"
			justify="center"
			px={3}
			bg="white"
			flexShrink={0}
			h="44px"
			borderBottomWidth={borderBottom ? '1px' : '0'}
			borderColor="gray.200"
			gap={1}
			wrap="nowrap"
		>
			{children}
		</Flex>
	);
}

function Divider() {
	return <Box w="1px" alignSelf="stretch" my="10px" bg="gray.200" mx="2px" flexShrink={0} />;
}

// ─── PDFSigner ────────────────────────────────────────────────────────────────
function PDFSigner({ isOpen = true, onClose = () => null, callback = () => null, onSave = null, url, showSign = false, showPreview = false }) {
	const rawUrl = url || SAMPLE_PDF_URL;
	const { t } = useTranslation();

	const LS_KEY = 'pdf_signer_zoom';
	const LS_SIG_KEY = 'pdf_signer_sig_pos';

	const [pdfUrl, setPdfUrl] = useState(null);
	const [loadProgress, setLoadProgress] = useState(null); // null = cached/done, else {loaded,total,percent}
	const [converting, setConverting] = useState(false);
	const [showLoading, setShowLoading] = useState(true); // overlay stays until first page renders
	const [loadingVisible, setLoadingVisible] = useState(true); // keeps DOM alive during fade-out
	const [pdfReady, setPdfReady] = useState(false); // true after first page painted — gates toolbar

	useEffect(() => {
		if (!isOpen || !rawUrl) return;
		let cancelled = false;
		let createdUrls = [];

		const finish = (blobUrl) => {
			if (cancelled) return;
			createdUrls.push(blobUrl);
			if (isImageUrl(rawUrl)) {
				setConverting(true);
				setPdfUrl(null);
				setLoadProgress(null);
				fetch(blobUrl)
					.then((r) => r.arrayBuffer())
					.then(async (buf) => {
						if (cancelled) return;
						const isPng = rawUrl.toLowerCase().includes('.png');
						const pdfDoc = await PDFDocument.create();
						const img = isPng ? await pdfDoc.embedPng(buf) : await pdfDoc.embedJpg(buf);
						const { width, height } = img.scale(1);
						const pg = pdfDoc.addPage([width, height]);
						pg.drawImage(img, { x: 0, y: 0, width, height });
						const outBytes = await pdfDoc.save();
						const converted = URL.createObjectURL(new Blob([outBytes], { type: 'application/pdf' }));
						createdUrls.push(converted);
						if (!cancelled) {
							setPdfUrl(converted);
							setConverting(false);
						}
					})
					.catch(() => {
						if (!cancelled) {
							setPdfUrl(blobUrl);
							setConverting(false);
						}
					});
			} else {
				setLoadProgress(null);
				setPdfUrl(blobUrl);
			}
		};

		(async () => {
			setPdfUrl(null);
			setLoadProgress(null);
			setShowLoading(true);
			setLoadingVisible(true);
			setPdfReady(false);
			// Try IndexedDB cache first
			const cached = await getCachedBlob(rawUrl);
			if (cached) {
				finish(cached);
				return;
			}
			// Download with progress
			setLoadProgress({ loaded: 0, total: 0, percent: null });
			try {
				const { blob } = await fetchWithProgress(rawUrl, (p) => {
					if (!cancelled) setLoadProgress(p);
				});
				const blobUrl = URL.createObjectURL(blob);
				setCachedBlob(rawUrl, blob); // write to cache in background
				finish(blobUrl);
			} catch {
				if (!cancelled) setPdfUrl(rawUrl); // fallback to direct URL
			}
		})();

		return () => {
			cancelled = true;
			createdUrls.forEach((u) => URL.revokeObjectURL(u));
		};
	}, [rawUrl, isOpen]);

	const [numPages, setNumPages] = useState(null);
	const [pageSize, setPageSize] = useState({ w: BASE_W, h: 1100 });
	const [currentPage, setCurrentPage] = useState(1);
	const [pageRotation, setPageRotation] = useState(0); // 0 | 90 | 180 | 270

	const rotatePage = () => setPageRotation((r) => (r + 90) % 360);
	const [zoom, setZoom] = useState(() => {
		const saved = parseFloat(localStorage.getItem(LS_KEY));
		return saved >= ZOOM_MIN && saved <= ZOOM_MAX ? saved : 1;
	});
	const [fitZoom, setFitZoom] = useState(1);
	const userSetZoomRef = useRef(false); // true once user has manually changed zoom
	const [signatures, setSignatures] = useState([]);
	const [letterHeads, setLetterHeads] = useState([]);
	// Rasterized date/title stickers (LetterDateTop/LetterDateButtom flattened to a
	// PNG once at insertion) — dragged/resized/stamped exactly like image stickers,
	// via dataUrl instead of a shared SIGNATURES url.
	const [htmlImageStickers, setHtmlImageStickers] = useState([]);
	const [activeId, setActiveId] = useState(null);
	const [downloading, setDownloading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [sigRatios, setSigRatios] = useState({}); // { [signatureId]: naturalWidth/naturalHeight }
	const [pickerOpen, setPickerOpen] = useState(false);
	const [pickerDate, setPickerDate] = useState(() => new Date()); // shared date, applies to whichever stamp (with a date) gets clicked
	const [pickerInserting, setPickerInserting] = useState(false); // true while rasterizing a date/title stamp — shows a spinner so the picker doesn't look frozen
	const [pickerWithLetterhead, setPickerWithLetterhead] = useState(true); // preview-mode-only checkbox: whether stamping also applies the letterhead
	const [letterHeadHeadRatio, setLetterHeadHeadRatio] = useState(8);
	const [letterHeadFootRatio, setLetterHeadFootRatio] = useState(8);
	const toast = useToast();
	const [uploadSignedPdf] = useMutation(UPLOAD_SIGNED_PDF_MUTATION);
	// Preview-only viewers don't have permission for the presigned signature
	// URLs this query returns, and preview mode never uses signature.url — skip it.
	const { data: dgSignQueryData } = useQuery(GET_DG_SIGN_DATA, { skip: !isOpen || showPreview, fetchPolicy: 'network-only' });

	// getDGSignData returns a JSON-encoded string of [{ id, url }] — presigned
	// URLs expire (X-Amz-Expires=3600), so this is re-fetched fresh each time
	// the modal opens rather than cached alongside the static SIGNATURE_META.
	const signUrlsById = useMemo(() => {
		const raw = dgSignQueryData?.getDGSignData;
		if (!raw) return {};
		try {
			const parsed = JSON.parse(raw);
			return Object.fromEntries(parsed.map(({ id, url }) => [id, url]));
		} catch (err) {
			console.error('Failed to parse getDGSignData', err);
			return {};
		}
	}, [dgSignQueryData]);

	// Merge static signature config with the dynamic, presigned URLs by id.
	const SIGNATURES = useMemo(() => SIGNATURE_META.map((sig) => ({ ...sig, url: signUrlsById[sig.id] || '' })), [signUrlsById]);

	const overlayRef = useRef(null);
	const canvasWrapRef = useRef(null); // the scrollable container
	const containerRef = useRef(null); // measures available width

	useEffect(() => {
		SIGNATURES.forEach((sig) => {
			// Preview mode has no permission to fetch the real presigned `url` —
			// use the public previewUrl instead (same image, same aspect ratio).
			const sizeUrl = showPreview ? sig.previewUrl : sig.url;
			if (!sizeUrl) return;
			loadImageSize(sizeUrl).then(({ w, h }) => setSigRatios((prev) => ({ ...prev, [sig.id]: w / h })));
		});
		loadImageSize(LETTERHEAD_HEAD).then(({ w, h }) => setLetterHeadHeadRatio(w / h));
		loadImageSize(LETTERHEAD_FOOT).then(({ w, h }) => setLetterHeadFootRatio(w / h));
	}, [SIGNATURES, showPreview]);

	useEffect(() => {
		if (isOpen) {
			setCurrentPage(1);
			setActiveId(null);
		}
	}, [isOpen]);

	// Lock body scroll & zoom when modal is open so background page can't scroll or
	// scale on mobile, and the modal truly occupies the full viewport.
	useEffect(() => {
		if (!isOpen) return;
		const prev = {
			overflow: document.body.style.overflow,
			position: document.body.style.position,
			width: document.body.style.width,
			touchAction: document.body.style.touchAction,
		};
		document.body.style.overflow = 'hidden';
		document.body.style.position = 'fixed';
		document.body.style.width = '100%';
		document.body.style.touchAction = 'none';
		return () => {
			document.body.style.overflow = prev.overflow;
			document.body.style.position = prev.position;
			document.body.style.width = prev.width;
			document.body.style.touchAction = prev.touchAction;
		};
	}, [isOpen]);

	// Push a dummy history entry when modal opens so swipe-back / browser back
	// closes the modal instead of navigating away.
	useEffect(() => {
		if (!isOpen) return;
		window.history.pushState({ pdfSignerModal: true }, '');
		const onPopState = () => {
			// User hit back — the dummy entry was just popped, so close the modal.
			onClose();
		};
		window.addEventListener('popstate', onPopState);
		return () => {
			window.removeEventListener('popstate', onPopState);
			// If modal closes via any other means (× button etc.), pop the dummy entry.
			if (window.history.state?.pdfSignerModal) window.history.back();
		};
	}, [isOpen]);

	// Compute fit-zoom: fit the full page (both width AND height) into the canvas area
	const computeFitZoom = useCallback(() => {
		if (!containerRef.current || !pageSize) return;
		const availW = containerRef.current.clientWidth - 4; // 2px border each side
		const availH = containerRef.current.clientHeight - 24; // py={3} top+bottom padding
		// react-pdf always renders the page at width=BASE_W regardless of rotation,
		// but when rotated 90/270 the rendered height becomes BASE_W*(w/h) instead of BASE_W*(h/w)
		const isLandscape = pageRotation === 90 || pageRotation === 270;
		const renderedH = isLandscape
			? BASE_W * (pageSize.w / pageSize.h) // rotated: original width becomes height
			: BASE_W * (pageSize.h / pageSize.w);
		const fzW = availW / BASE_W;
		const fzH = availH / renderedH;
		// use the smaller so the page fits in both dimensions without scrolling
		const fz = parseFloat(Math.max(ZOOM_MIN, Math.min(fzW, fzH)).toFixed(2));
		setFitZoom(fz);
		// Only apply fit-zoom on first load; if user already has a saved zoom, keep it
		if (!userSetZoomRef.current) {
			const saved = parseFloat(localStorage.getItem(LS_KEY));
			const initial = saved >= ZOOM_MIN && saved <= ZOOM_MAX ? saved : fz;
			setZoom(initial);
		}
	}, [pageSize, pageRotation]);

	useEffect(() => {
		computeFitZoom();
		const ro = new ResizeObserver(computeFitZoom);
		if (containerRef.current) ro.observe(containerRef.current);
		return () => ro.disconnect();
	}, [computeFitZoom, isOpen]);

	const onDocumentLoadSuccess = useCallback(({ numPages: n }) => setNumPages(n), []);

	const onPageLoadSuccess = useCallback(
		(page) => {
			setPageSize({ w: page.originalWidth, h: page.originalHeight });
			computeFitZoom();
			setPdfReady(true);
			setShowLoading(false); // trigger fade-out only after page is actually painted
		},
		[computeFitZoom],
	);

	const goTo = (p) => {
		setCurrentPage(Math.min(Math.max(1, p), numPages || 1));
		setActiveId(null);
	};

	// Opens the signature picker; the actual insertion happens in insertSignature
	// once the user picks which signature image to use.
	const [isLoadingSignModel, setIsLoadingSignModel] = useState(false);
	const addSignature = () => {
		setIsLoadingSignModel(true);
		setTimeout(() => {
			setPickerOpen(true);
		}, 200);
		setTimeout(() => {
			setIsLoadingSignModel(false);
		}, 3000);
	};

	const insertSignature = async (signature, pickedDate = null) => {
		// Signatures with a date/title stamp take a moment to rasterize — keep the
		// picker open with a spinner instead of closing immediately, so it's clear
		// something is happening rather than looking frozen/unresponsive.
		if (signature.HtmlComponent) {
			setPickerInserting(true);
		} else {
			setPickerOpen(false);
		}
		// Always reset rotation to 0 when signing so coords are always in unrotated space.
		setPageRotation(0);
		const origH = BASE_W * (pageSize.h / pageSize.w);
		const currentRatio = pageSize.h / pageSize.w;
		const ratio = sigRatios[signature.id] || 2;
		// Width always comes fresh from the signature's configured percentage — never
		// restored from storage, so switching signatures (or updating widths) always applies.
		const widthPct = parseFloat(signature.width) / 100 || 0.2;
		const w = Math.round(BASE_W * widthPct);

		// Restore saved position (not size) if page aspect ratio + signature match
		let x, y, rotation;
		try {
			const saved = JSON.parse(localStorage.getItem(LS_SIG_KEY));
			if (saved && saved.signatureId === signature.id && Math.abs(saved.pageRatio - currentRatio) < 0.01) {
				x = saved.x;
				y = saved.y;
				rotation = saved.rotation;
			}
		} catch (_) {}

		if (x == null) {
			x = BASE_W / 2 - w / 2;
			y = origH / 2 - w / ratio / 2;
			rotation = 0;
		}

		const id = Date.now();
		setSignatures((prev) => [...prev, { id, signatureId: signature.id, page: currentPage, x, y, w, ratio, rotation }]);

		// Preview mode has no manual "Letterhead" button — the picker's
		// "Letterhead" checkbox decides whether stamping also applies it.
		// Runs before setActiveId(id) below since addLetterHead clears activeId.
		if (showPreview && pickerWithLetterhead) {
			addLetterHead();
		}
		setActiveId(id);

		// Companion text block (date/title etc.) — flattened to a plain PNG once,
		// right now, via html2canvas. From this point on it's just an image sticker:
		// dragged/resized/stamped exactly like a signature, no more live text reflow
		// to keep in sync between the editor and the saved PDF.
		if (signature.HtmlComponent) {
			const htmlX = (parseFloat(signature.htmlX) / 100 || 0.1) * BASE_W;
			const htmlY = (parseFloat(signature.htmlY) / 100 || 0.1) * origH;
			const htmlW = Math.round((parseFloat(signature.htmlW) / 100 || 0.2) * BASE_W);
			const htmlId = id + 1;

			try {
				const { url, ratio: htmlRatio } = await rasterizeHtmlToImage(<signature.HtmlComponent date={pickedDate} />);
				setHtmlImageStickers((prev) => [
					...prev,
					{ id: htmlId, page: currentPage, x: htmlX, y: htmlY, w: htmlW, ratio: htmlRatio, rotation: 0, dataUrl: url },
				]);
			} catch (err) {
				console.error('Failed to rasterize signature date/title block', err);
			} finally {
				setPickerInserting(false);
				setPickerOpen(false);
			}
		}
	};

	const updateSig = useCallback(
		(id, patch) => {
			setSignatures((prev) =>
				prev.map((s) => {
					if (s.id !== id) return s;
					const updated = { ...s, ...patch };
					try {
						localStorage.setItem(
							LS_SIG_KEY,
							JSON.stringify({
								x: updated.x,
								y: updated.y,
								rotation: updated.rotation,
								signatureId: updated.signatureId,
								pageRatio: pageSize.h / pageSize.w,
							}),
						);
					} catch (_) {}
					return updated;
				}),
			);
		},
		[pageSize],
	);

	const removeSig = useCallback((id) => {
		setSignatures((prev) => prev.filter((s) => s.id !== id));
		setActiveId(null);
	}, []);

	const updateHtmlImageSticker = useCallback((id, patch) => {
		setHtmlImageStickers((prev) => prev.map((h) => (h.id === id ? { ...h, ...patch } : h)));
	}, []);

	const removeHtmlImageSticker = useCallback((id) => {
		setHtmlImageStickers((prev) => prev.filter((h) => h.id !== id));
		setActiveId(null);
	}, []);

	// Inserts both header and footer, full-width, pinned to top and bottom of the current page.
	const addLetterHead = () => {
		setPageRotation(0);
		const origH = BASE_W * (pageSize.h / pageSize.w);
		const w = BASE_W;
		const headH = w / letterHeadHeadRatio;
		const footH = w / letterHeadFootRatio;
		const headId = Date.now();
		const footId = headId + 1;

		// Only one of each kind per page — replace existing instead of stacking duplicates.
		setLetterHeads((prev) => {
			const withoutCurrentPage = prev.filter((lh) => lh.page !== currentPage);
			return [
				...withoutCurrentPage,
				{ id: headId, kind: 'head', page: currentPage, x: 0, y: 0, w, ratio: letterHeadHeadRatio, rotation: 0 },
				{ id: footId, kind: 'foot', page: currentPage, x: 0, y: origH - footH, w, ratio: letterHeadFootRatio, rotation: 0 },
			];
		});
		setActiveId(null);
	};

	const updateLetterHead = useCallback((id, patch) => {
		setLetterHeads((prev) => prev.map((lh) => (lh.id === id ? { ...lh, ...patch } : lh)));
	}, []);

	const removeLetterHead = useCallback((id) => {
		setLetterHeads((prev) => prev.filter((lh) => lh.id !== id));
		setActiveId(null);
	}, []);

	const applyZoom = useCallback((z) => {
		const clamped = parseFloat(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z)).toFixed(2));
		userSetZoomRef.current = true;
		localStorage.setItem(LS_KEY, String(clamped));
		setZoom(clamped);
	}, []);
	const zoomIn = () => applyZoom(zoom + ZOOM_STEP);
	const zoomOut = () => applyZoom(zoom - ZOOM_STEP);
	const zoomFit = () => {
		userSetZoomRef.current = false;
		localStorage.removeItem(LS_KEY);
		setZoom(fitZoom);
	};

	// ── Keyboard shortcuts ────────────────────────────────────────────────────
	// Ctrl/Cmd + = / + → zoom in   Ctrl/Cmd + - → zoom out   Ctrl/Cmd + 0 → fit
	// ArrowRight / ArrowLeft → next / prev page
	useEffect(() => {
		if (!isOpen) return;
		const onKey = (e) => {
			// Arrow keys — page navigation (no modifier needed)
			if (e.key === 'ArrowRight') {
				e.preventDefault();
				setCurrentPage((p) => Math.min(p + 1, numPages || 1));
				setActiveId(null);
				return;
			}
			if (e.key === 'ArrowLeft') {
				e.preventDefault();
				setCurrentPage((p) => Math.max(p - 1, 1));
				setActiveId(null);
				return;
			}

			// Zoom — require Ctrl or Cmd
			if (!e.ctrlKey && !e.metaKey) return;
			if (e.key === '=' || e.key === '+' || e.code === 'Equal') {
				e.preventDefault();
				setZoom((z) => {
					const next = parseFloat(Math.min(ZOOM_MAX, z + ZOOM_STEP).toFixed(2));
					userSetZoomRef.current = true;
					localStorage.setItem(LS_KEY, String(next));
					return next;
				});
			} else if (e.key === '-' || e.code === 'Minus') {
				e.preventDefault();
				setZoom((z) => {
					const next = parseFloat(Math.max(ZOOM_MIN, z - ZOOM_STEP).toFixed(2));
					userSetZoomRef.current = true;
					localStorage.setItem(LS_KEY, String(next));
					return next;
				});
			} else if (e.key === '0') {
				e.preventDefault();
				zoomFit();
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [isOpen, zoomFit, numPages]);

	// ── Wheel / trackpad pinch: Ctrl+wheel ────────────────────────────────────
	useEffect(() => {
		if (!isOpen) return;
		const el = containerRef.current;
		if (!el) return;
		const onWheel = (e) => {
			if (!e.ctrlKey && !e.metaKey) return; // normal scroll passes through
			e.preventDefault();
			// deltaY is negative when zooming in (pinch-open / scroll-up)
			const delta = -e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.002);
			setZoom((z) => {
				const next = parseFloat(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + delta)).toFixed(3));
				userSetZoomRef.current = true;
				localStorage.setItem(LS_KEY, String(next));
				return next;
			});
		};
		// passive:false required to call preventDefault on wheel
		el.addEventListener('wheel', onWheel, { passive: false });
		return () => el.removeEventListener('wheel', onWheel);
	}, [isOpen]);

	// ── Touch pinch-to-zoom ───────────────────────────────────────────────────
	// We use a CSS custom property --pz on the scroll container to drive zoom.
	// During gesture: write --pz directly (no React re-render = 60fps, no flicker).
	// On touchend: commit to React state. React re-render writes the same --pz value
	// via useEffect, so there is never a frame where the value is wrong.
	const pinchRef = useRef(null);
	const innerBoxRef = useRef(null);
	const outerBoxRef = useRef(null);
	const liveZoomRef = useRef(zoom); // always tracks current zoom without closure staleness

	// Keep liveZoomRef in sync with zoom state
	useEffect(() => {
		liveZoomRef.current = zoom;
	}, [zoom]);

	// Write --pz to the container whenever zoom state changes (covers button clicks too)
	useEffect(() => {
		if (!containerRef.current) return;
		// react-pdf renders width=BASE_W always; height flips when rotated 90/270
		const isLandscape = pageRotation === 90 || pageRotation === 270;
		const renderedH = isLandscape ? BASE_W * (pageSize.w / pageSize.h) : BASE_W * (pageSize.h / pageSize.w);
		containerRef.current.style.setProperty('--pz', zoom);
		containerRef.current.style.setProperty('--pw', `${Math.round(BASE_W * zoom)}px`);
		containerRef.current.style.setProperty('--ph', `${Math.round(renderedH * zoom)}px`);
	}, [zoom, pageSize, pageRotation]);

	useEffect(() => {
		if (!isOpen) return;
		const el = containerRef.current;
		if (!el) return;

		const getDist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

		const setLiveZoom = (next) => {
			const isLandscape = pageRotation === 90 || pageRotation === 270;
			const renderedH = isLandscape ? BASE_W * (pageSize.w / pageSize.h) : BASE_W * (pageSize.h / pageSize.w);
			el.style.setProperty('--pz', next);
			el.style.setProperty('--pw', `${Math.round(BASE_W * next)}px`);
			el.style.setProperty('--ph', `${Math.round(renderedH * next)}px`);
		};

		const onTouchStart = (e) => {
			if (e.touches.length !== 2) return;
			e.preventDefault();
			const t = e.touches;
			const midX = (t[0].clientX + t[1].clientX) / 2;
			const midY = (t[0].clientY + t[1].clientY) / 2;
			const elRect = el.getBoundingClientRect();
			const cardRect = outerBoxRef.current ? outerBoxRef.current.getBoundingClientRect() : elRect;
			pinchRef.current = {
				startDist: getDist(t),
				startZoom: liveZoomRef.current,
				fingerViewportX: midX - elRect.left,
				fingerViewportY: midY - elRect.top,
				cardLeft: el.scrollLeft + cardRect.left - elRect.left,
				cardTop: el.scrollTop + cardRect.top - elRect.top,
				pointInCardX: midX - cardRect.left,
				pointInCardY: midY - cardRect.top,
			};
		};

		const onTouchMove = (e) => {
			if (e.touches.length !== 2 || !pinchRef.current) return;
			e.preventDefault();
			const p = pinchRef.current;
			const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, p.startZoom * (getDist(e.touches) / p.startDist)));

			// Update CSS vars — drives transform and card size via CSS, zero React
			setLiveZoom(next);
			liveZoomRef.current = next;

			// Anchor scroll to pinch midpoint
			const r = next / p.startZoom;
			el.scrollLeft = (p.cardLeft + p.pointInCardX) * r - p.fingerViewportX;
			el.scrollTop = (p.cardTop + p.pointInCardY) * r - p.fingerViewportY;

			pinchRef.current._next = next;
		};

		const onTouchEnd = (e) => {
			if (e.touches.length >= 2) return;
			if (pinchRef.current?._next != null) {
				const committed = parseFloat(pinchRef.current._next.toFixed(3));
				// CSS vars already show the correct state — setZoom just syncs React state.
				// No DOM mutation needed here; the vars are already right.
				userSetZoomRef.current = true;
				localStorage.setItem(LS_KEY, String(committed));
				setZoom(committed);
			}
			pinchRef.current = null;
		};

		el.addEventListener('touchstart', onTouchStart, { passive: false });
		el.addEventListener('touchmove', onTouchMove, { passive: false });
		el.addEventListener('touchend', onTouchEnd, { passive: true });
		return () => {
			el.removeEventListener('touchstart', onTouchStart);
			el.removeEventListener('touchmove', onTouchMove);
			el.removeEventListener('touchend', onTouchEnd);
		};
	}, [isOpen, zoom, pageSize]);

	// Sniffs the actual file signature (PNG magic bytes vs JPEG SOI marker) so
	// letterhead/signature images can be swapped between formats without needing
	// to touch this code — trusting the URL extension isn't reliable enough.
	const embedImageAuto = (pdfDoc, bytes) => {
		const bytesArr = new Uint8Array(bytes);
		const isPng = bytesArr.length >= 8 && bytesArr[0] === 0x89 && bytesArr[1] === 0x50 && bytesArr[2] === 0x4e && bytesArr[3] === 0x47;
		return isPng ? pdfDoc.embedPng(bytes) : pdfDoc.embedJpg(bytes);
	};

	// Stamps a list of positioned stickers (signatures or letterhead pieces) sharing
	// one source image onto the given pdf-lib pages. Coordinates are in unrotated
	// page space (BASE_W × origLogicalH), same convention as the on-screen overlay.
	const stampImageStickers = (pdfPages, stickers, image) => {
		for (const item of stickers) {
			const idx = item.page - 1;
			if (idx < 0 || idx >= pdfPages.length) continue;
			const pdfPage = pdfPages[idx];
			// getSize() returns effective (rotated) size if PDF has its own rotation.
			// We work in the display-rotated space, so use BASE_W × logicalPageH as our space.
			const { width: pW, height: pH } = pdfPage.getSize();

			const itemW = item.w;
			const itemH = item.w / item.ratio;
			const scale = pW / BASE_W;
			const imgW = itemW * scale;
			const imgH = itemH * scale;
			const pdfX = item.x * scale;
			const pdfY = pH - (item.y + itemH) * scale;

			const rad = (item.rotation * Math.PI) / 180;
			const cos = Math.cos(rad);
			const sin = Math.sin(rad);
			const hx = imgW / 2;
			const hy = imgH / 2;
			const rotHx = hx * cos - hy * sin;
			const rotHy = hx * sin + hy * cos;

			pdfPage.drawImage(image, {
				x: pdfX + hx - rotHx,
				y: pdfY + hy - rotHy,
				width: imgW,
				height: imgH,
				rotate: degrees(item.rotation),
			});
		}
	};

	// Stamps all signatures, letterhead pieces, and rasterized html-image stickers
	// onto the loaded PDF and returns the resulting Blob. Returns null when there
	// is nothing to stamp.
	const buildSignedPdfBlob = async () => {
		if (signatures.length === 0 && letterHeads.length === 0 && htmlImageStickers.length === 0) return null;

		const pdfBytes = await fetch(pdfUrl).then((r) => r.arrayBuffer());
		const pdfDoc = await PDFDocument.load(pdfBytes);
		const pdfPages = pdfDoc.getPages();

		// Stamping order = paint order in the final PDF (pdf-lib has no concept of
		// CSS z-index — whatever is drawn later covers whatever was drawn before).
		// Must match the on-screen stacking: letterheads (bottom) → signatures
		// (middle) → html date/title stickers (top), otherwise the letterhead would
		// visually cover the date stamp in the saved file even though the editor
		// shows it on top.
		const headStickers = letterHeads.filter((lh) => lh.kind === 'head');
		if (headStickers.length > 0) {
			const headBytes = await fetch(LETTERHEAD_HEAD).then((r) => r.arrayBuffer());
			const headImage = await embedImageAuto(pdfDoc, headBytes);
			stampImageStickers(pdfPages, headStickers, headImage);
		}

		const footStickers = letterHeads.filter((lh) => lh.kind === 'foot');
		if (footStickers.length > 0) {
			const footBytes = await fetch(LETTERHEAD_FOOT).then((r) => r.arrayBuffer());
			const footImage = await embedImageAuto(pdfDoc, footBytes);
			stampImageStickers(pdfPages, footStickers, footImage);
		}

		for (const signature of SIGNATURES) {
			const stickers = signatures.filter((s) => s.signatureId === signature.id);
			if (stickers.length === 0) continue;
			// Preview mode uses the public previewUrl to stamp — a preview viewer
			// has no permission to fetch the real presigned signature `url`.
			const stampUrl = showPreview ? signature.previewUrl : signature.url;
			const sigBytes = await fetch(stampUrl).then((r) => r.arrayBuffer());
			const sigImage = await embedImageAuto(pdfDoc, sigBytes);
			stampImageStickers(pdfPages, stickers, sigImage);
		}

		for (const htmlSticker of htmlImageStickers) {
			const htmlBytes = await fetch(htmlSticker.dataUrl).then((r) => r.arrayBuffer());
			const htmlImage = await pdfDoc.embedPng(htmlBytes);
			stampImageStickers(pdfPages, [htmlSticker], htmlImage);
		}

		const outBytes = await pdfDoc.save();
		return new Blob([outBytes], { type: 'application/pdf' });
	};

	const printPdf = async () => {
		try {
			let blob = await buildSignedPdfBlob();
			if (!blob) {
				const pdfBytes = await fetch(pdfUrl).then((r) => r.arrayBuffer());
				const pdfDoc = await PDFDocument.load(pdfBytes);
				const outBytes = await pdfDoc.save();
				blob = new Blob([outBytes], { type: 'application/pdf' });
			}
			const url = URL.createObjectURL(blob);
			const iframe = document.createElement('iframe');
			iframe.style.display = 'none';
			iframe.src = url;
			document.body.appendChild(iframe);
			iframe.onload = () => {
				setTimeout(() => {
					iframe.contentWindow?.focus();
					const cleanup = () => {
						document.body.removeChild(iframe);
						URL.revokeObjectURL(url);
						window.removeEventListener('afterprint', cleanup);
					};
					window.addEventListener('afterprint', cleanup);
					iframe.contentWindow?.print();
				}, 1000);
			};
		} catch (error) {
			console.error('Print error', error);
		}
	};

	const downloadSigned = async () => {
		console.log('Downloading signed PDF...', pdfUrl);
		setDownloading(true);
		try {
			const blob = await buildSignedPdfBlob();
			// No signatures — download original file directly
			if (!blob) {
				const link = document.createElement('a');
				link.href = pdfUrl;
				link.download = 'document.pdf';
				link.click();
				callback(null);
				return;
			}

			const link = document.createElement('a');
			link.href = URL.createObjectURL(blob);
			link.download = 'signed.pdf';
			link.click();
			URL.revokeObjectURL(link.href);
			callback(blob);
		} catch (err) {
			console.error('PDF sign error', err);
		} finally {
			setDownloading(false);
		}
	};

	const saveSigned = async () => {
		if (!onSave) return;
		setSaving(true);
		try {
			const blob = await buildSignedPdfBlob();
			if (!blob) {
				toast({
					title: t('Add a signature before saving'),
					status: 'warning',
					isClosable: true,
					position: 'bottom-right',
				});
				return;
			}

			const file = new File([blob], 'signed.pdf', { type: 'application/pdf' });
			const { data } = await uploadSignedPdf({
				variables: { type: 'np', fileSize: file.size, file },
				context: {
					fetchOptions: {
						useUpload: true,
						onProgress: () => {},
						onAbortPossible: () => {},
					},
				},
			});

			if (data?.userUploadTempFile?.id) {
				await onSave({ id: data.userUploadTempFile.id, blob });
				toast({
					title: t('Signed PDF saved'),
					status: 'success',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		} catch (err) {
			console.error('PDF save error', err);
			toast({
				title: t('Failed to save signed PDF'),
				status: 'error',
				isClosable: true,
				position: 'bottom-right',
			});
		} finally {
			setSaving(false);
		}
	};

	const pageSigs = signatures.filter((s) => s.page === currentPage);
	const pageLetterHeads = letterHeads.filter((lh) => lh.page === currentPage);
	const pageHtmlImageStickers = htmlImageStickers.filter((h) => h.page === currentPage);
	// Unrotated page height — sig coords always live here
	const origLogicalH = BASE_W * (pageSize.h / pageSize.w);
	// Rendered height after react-pdf applies rotation (used only for --ph CSS var)
	const isPageLandscape = pageRotation === 90 || pageRotation === 270;
	const logicalPageH = isPageLandscape ? BASE_W * (pageSize.w / pageSize.h) : origLogicalH;

	return (
		<>
			<Modal isOpen={isOpen} onClose={onClose} size="full" scrollBehavior="inside">
				<ModalOverlay />
				<ModalContent
					borderRadius={0}
					pt="0"
					m={0}
					style={{
						// Use fixed + inset:0 so it truly fills the viewport regardless of
						// browser chrome, Chakra defaults, or orientation changes.
						position: 'fixed',
						inset: 0,
						width: '100dvw',
						height: '100dvh',
						maxWidth: '100dvw',
						maxHeight: '100dvh',
						paddingBottom: 'env(safe-area-inset-bottom, 0px)',
					}}
					display="flex"
					flexDir="column"
					overflow="hidden"
				>
					{/* ══ DESKTOP (md+): single navbar + absolute close button ══ */}
					<Box display={{ base: 'none', md: 'block' }} position="relative">
						<IconButton
							icon={<FontAwesomeIcon icon={faTimes} />}
							aria-label="Close"
							onClick={onClose}
							position="absolute"
							top="6px"
							right="8px"
							zIndex={10}
							size="sm"
							borderRadius="full"
							variant="ghost"
							fontSize="20px"
						/>
						<NavBar>
							<TB
								icon={<FontAwesomeIcon icon={faRedo} />}
								aria-label="Rotate page"
								isDisabled={!pdfReady}
								onClick={rotatePage}
								title="Rotate page 90°"
							/>
							<TB
								icon={<FontAwesomeIcon icon={faPrint} />}
								aria-label="Print"
								isLoading={false}
								isDisabled={!pdfReady}
								onClick={printPdf}
								title="Print"
							/>
							<TB
								icon={<FontAwesomeIcon icon={faDownload} />}
								aria-label="Download"
								isLoading={downloading}
								isDisabled={!pdfReady}
								onClick={downloadSigned}
								title="Download"
							/>
							<Divider />

							<TB
								icon={<FontAwesomeIcon icon={faChevronLeft} />}
								aria-label="Previous"
								isDisabled={!pdfReady || currentPage <= 1}
								onClick={() => goTo(currentPage - 1)}
								w="40px"
								sx={{ _disabled: { opacity: 0.35, cursor: 'default' } }}
							/>
							<select
								value={currentPage}
								onChange={(e) => goTo(Number(e.target.value))}
								disabled={!pdfReady}
								style={{
									fontSize: 12,
									fontWeight: 600,
									color: '#2d3748',
									background: 'transparent',
									border: 'none',
									outline: 'none',
									cursor: 'pointer',
									flexShrink: 0,
									textAlign: 'center',
									width: '72px',
									margin: '0 4px',
								}}
							>
								{Array.from({ length: numPages || 1 }, (_, i) => (
									<option key={i + 1} value={i + 1}>
										{i + 1} / {numPages}
									</option>
								))}
							</select>
							<TB
								icon={<FontAwesomeIcon icon={faChevronRight} />}
								aria-label="Next"
								isDisabled={!pdfReady || currentPage >= numPages}
								onClick={() => goTo(currentPage + 1)}
								w="40px"
								sx={{ _disabled: { opacity: 0.35, cursor: 'default' } }}
							/>

							<Divider />

							<TB icon={<FontAwesomeIcon icon={faSearchMinus} />} aria-label="Zoom out" isDisabled={zoom <= ZOOM_MIN} onClick={zoomOut} />
							<Text
								fontSize="xs"
								color="gray.600"
								w="38px"
								textAlign="center"
								cursor="pointer"
								flexShrink={0}
								onClick={zoomFit}
								title="Click to fit"
							>
								{Math.round(zoom * 100)}%
							</Text>
							<TB icon={<FontAwesomeIcon icon={faSearchPlus} />} aria-label="Zoom in" isDisabled={zoom >= ZOOM_MAX} onClick={zoomIn} />

							{showPreview && (
								<>
									<Divider />
									<Button
										isLoading={isLoadingSignModel}
										colorScheme="teal"
										size="sm"
										borderRadius="full"
										leftIcon={<FontAwesomeIcon icon={faEye} />}
										isDisabled={!pdfReady}
										onClick={addSignature}
										mr={2}
										sx={{ _disabled: { opacity: 1, cursor: 'default' } }}
									>
										{t('Preview')}
									</Button>
								</>
							)}

							{showSign && (
								<>
									<Divider />
									<Button
										isLoading={isLoadingSignModel}
										colorScheme="blue"
										size="sm"
										borderRadius="full"
										leftIcon={<FontAwesomeIcon icon={faPen} />}
										isDisabled={!pdfReady}
										onClick={addSignature}
										mr={2}
										sx={{ _disabled: { opacity: 1, cursor: 'default' } }}
									>
										{t('Sign')}
									</Button>
									{/* <Button colorScheme="purple" size="sm" borderRadius="full"
                            leftIcon={<FontAwesomeIcon icon={faFileAlt} />}
                            isDisabled={!pdfReady} onClick={addLetterHead}
                            mr={onSave ? 2 : 0}
                            sx={{ _disabled: { opacity: 1, cursor: "default" } }}>{t("Letterhead")}</Button> */}
									{onSave && (
										<Button
											colorScheme="green"
											size="sm"
											borderRadius="full"
											leftIcon={<FontAwesomeIcon icon={faSave} />}
											isLoading={saving}
											isDisabled={!pdfReady}
											onClick={saveSigned}
											sx={{ _disabled: { opacity: 1, cursor: 'default' } }}
										>
											{t('Save')}
										</Button>
									)}
								</>
							)}
						</NavBar>
					</Box>

					{/* ══ MOBILE (< md): single row, space-between ══
                    Left: rotate + download
                    Center: prev · page · next · zoom− · % · zoom+
                    Right: X close                                    */}
					<Box display={{ base: 'block', md: 'none' }}>
						<Flex align="center" px={1} bg="white" flexShrink={0} h="44px" borderBottomWidth="1px" borderColor="gray.200" justify="space-between">
							{/* Left group */}
							<Flex align="center" gap={0} flexShrink={0}>
								<TB icon={<FontAwesomeIcon icon={faRedo} />} aria-label="Rotate page" isDisabled={!pdfReady} onClick={rotatePage} />
								<TB
									icon={<FontAwesomeIcon icon={faDownload} />}
									aria-label="Download"
									isLoading={downloading}
									isDisabled={!pdfReady}
									onClick={downloadSigned}
								/>
							</Flex>

							{/* Center group */}
							<Flex align="center" gap={0} flexShrink={0}>
								<TB
									icon={<FontAwesomeIcon icon={faChevronLeft} />}
									aria-label="Previous"
									isDisabled={!pdfReady || currentPage <= 1}
									onClick={() => goTo(currentPage - 1)}
									w="36px"
									sx={{ _disabled: { opacity: 0.35, cursor: 'default' } }}
								/>
								<select
									value={currentPage}
									onChange={(e) => goTo(Number(e.target.value))}
									disabled={!pdfReady}
									style={{
										fontSize: 11,
										fontWeight: 600,
										color: '#2d3748',
										background: 'transparent',
										border: 'none',
										outline: 'none',
										cursor: 'pointer',
										flexShrink: 0,
										textAlign: 'center',
										width: '64px',
										margin: '0 2px',
									}}
								>
									{Array.from({ length: numPages || 1 }, (_, i) => (
										<option key={i + 1} value={i + 1}>
											{i + 1} / {numPages}
										</option>
									))}
								</select>
								<TB
									icon={<FontAwesomeIcon icon={faChevronRight} />}
									aria-label="Next"
									isDisabled={!pdfReady || currentPage >= numPages}
									onClick={() => goTo(currentPage + 1)}
									w="36px"
									sx={{ _disabled: { opacity: 0.35, cursor: 'default' } }}
								/>
								<Divider />
								<TB icon={<FontAwesomeIcon icon={faSearchMinus} />} aria-label="Zoom out" isDisabled={zoom <= ZOOM_MIN} onClick={zoomOut} />
								<Text fontSize="xs" color="gray.600" w="36px" textAlign="center" cursor="pointer" flexShrink={0} onClick={zoomFit}>
									{Math.round(zoom * 100)}%
								</Text>
								<TB icon={<FontAwesomeIcon icon={faSearchPlus} />} aria-label="Zoom in" isDisabled={zoom >= ZOOM_MAX} onClick={zoomIn} />
							</Flex>

							{/* Right group */}
							<Flex align="center" flexShrink={0}>
								<TB icon={<FontAwesomeIcon icon={faTimes} />} aria-label="Close" onClick={onClose} fontSize="18px" />
							</Flex>
						</Flex>

						{/* Second row — Preview / Sign / Save, full-size buttons for easy tapping */}
						{showPreview && (
							<Flex
								align="center"
								justify="center"
								px={3}
								py={2}
								bg="white"
								flexShrink={0}
								borderBottomWidth="1px"
								borderColor="gray.200"
								gap={3}
							>
								<Button
									colorScheme="teal"
									size="sm"
									borderRadius="full"
									flex={1}
									maxW="140px"
									leftIcon={<FontAwesomeIcon icon={faEye} />}
									isDisabled={!pdfReady}
									onClick={addSignature}
									sx={{ _disabled: { opacity: 1, cursor: 'default' } }}
								>
									{t('Preview')}
								</Button>
							</Flex>
						)}
						{showSign && (
							<Flex
								align="center"
								justify="center"
								px={3}
								py={2}
								bg="white"
								flexShrink={0}
								borderBottomWidth="1px"
								borderColor="gray.200"
								gap={3}
							>
								<Button
									colorScheme="blue"
									size="sm"
									borderRadius="full"
									flex={1}
									maxW="140px"
									leftIcon={<FontAwesomeIcon icon={faPen} />}
									isDisabled={!pdfReady}
									onClick={addSignature}
									sx={{ _disabled: { opacity: 1, cursor: 'default' } }}
								>
									Sign
								</Button>
								<Button
									colorScheme="purple"
									size="sm"
									borderRadius="full"
									flex={1}
									maxW="140px"
									leftIcon={<FontAwesomeIcon icon={faFileAlt} />}
									isDisabled={!pdfReady}
									onClick={addLetterHead}
									sx={{ _disabled: { opacity: 1, cursor: 'default' } }}
								>
									{t('Letter Head')}
								</Button>
								{onSave && (
									<Button
										colorScheme="green"
										size="sm"
										borderRadius="full"
										flex={1}
										maxW="140px"
										leftIcon={<FontAwesomeIcon icon={faSave} />}
										isLoading={saving}
										isDisabled={!pdfReady}
										onClick={saveSigned}
										sx={{ _disabled: { opacity: 1, cursor: 'default' } }}
									>
										{t('Save')}
									</Button>
								)}
							</Flex>
						)}
					</Box>

					{/* ── Canvas area ── */}
					<Box
						ref={containerRef}
						flex="1"
						overflow="auto"
						bg="gray.300"
						position="relative"
						style={{ WebkitOverflowScrolling: 'touch' }}
						onClick={(e) => {
							if (e.target === e.currentTarget) setActiveId(null);
						}}
					>
						{/*
						 * Centering trick that works on overflow:
						 * - inner div is at least 100% wide (so small pages center)
						 * - display:flex + justifyContent:"safe center" prevents
						 *   left-clip on overflow (safe = start-align when overflowing)
						 * - py keeps top/bottom padding
						 */}
						<div
							style={{
								minWidth: '100%',
								minHeight: '100%',
								display: 'inline-flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'flex-start',
								paddingTop: '12px',
								paddingBottom: '12px',
								boxSizing: 'border-box',
							}}
						>
							{pdfUrl && !converting && (
								<Document file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess} loading={null}>
									{numPages && (
										<Box
											ref={outerBoxRef}
											position="relative"
											overflow="hidden"
											style={{
												border: PAGE_BORDER,
												width: 'var(--pw)',
												height: 'var(--ph)',
											}}
											flexShrink={0}
										>
											{/* Inner box: matches rendered (rotated) page dims */}
											<Box
												ref={innerBoxRef}
												position="absolute"
												top={0}
												left={0}
												style={{
													width: `${BASE_W}px`,
													height: `${logicalPageH}px`,
													transform: 'scale(var(--pz))',
													transformOrigin: 'top left',
												}}
											>
												<Page
													pageNumber={currentPage}
													width={BASE_W}
													rotate={pageRotation}
													renderTextLayer={false}
													renderAnnotationLayer={false}
													onLoadSuccess={onPageLoadSuccess}
												/>

												{/* Signature overlay — only visible at rotation 0 where coords are valid */}
												{pageRotation === 0 && (
													<Box
														ref={overlayRef}
														position="absolute"
														top={0}
														left={0}
														width={`${BASE_W}px`}
														height={`${origLogicalH}px`}
														onClick={(e) => {
															if (e.target === e.currentTarget) setActiveId(null);
														}}
													>
														{pageLetterHeads.map((lh) => (
															<SigSticker
																key={lh.id}
																sig={lh}
																src={lh.kind === 'head' ? LETTERHEAD_HEAD : LETTERHEAD_FOOT}
																allowRotate={false}
																zIndex={1}
																isActive={activeId === lh.id}
																overlayRef={overlayRef}
																zoom={zoom}
																pageH={origLogicalH}
																onChange={(patch) => updateLetterHead(lh.id, patch)}
																onRemove={() => removeLetterHead(lh.id)}
																onActivate={() => setActiveId(lh.id)}
															/>
														))}
														{pageSigs.map((sig) => (
															<SigSticker
																key={sig.id}
																sig={sig}
																src={
																	showPreview
																		? SIGNATURES.find((s) => s.id === sig.signatureId)?.previewUrl
																		: SIGNATURES.find((s) => s.id === sig.signatureId)?.url
																}
																zIndex={2}
																isActive={activeId === sig.id}
																overlayRef={overlayRef}
																zoom={zoom}
																pageH={origLogicalH}
																onChange={(patch) => updateSig(sig.id, patch)}
																onRemove={() => removeSig(sig.id)}
																onActivate={() => setActiveId(sig.id)}
															/>
														))}
														{pageHtmlImageStickers.map((h) => (
															<SigSticker
																key={h.id}
																sig={h}
																src={h.dataUrl}
																zIndex={3}
																isActive={activeId === h.id}
																overlayRef={overlayRef}
																zoom={zoom}
																pageH={origLogicalH}
																onChange={(patch) => updateHtmlImageSticker(h.id, patch)}
																onRemove={() => removeHtmlImageSticker(h.id)}
																onActivate={() => setActiveId(h.id)}
															/>
														))}
													</Box>
												)}
											</Box>
										</Box>
									)}
								</Document>
							)}
						</div>

						{/* Loading overlay — anchored to canvas area, sits above Document until first page renders */}
						{loadingVisible && (
							<Flex
								justify="center"
								align="center"
								flexDir="column"
								gap={2}
								style={{
									position: 'absolute',
									inset: 0,
									background: 'white',
									zIndex: 2,
									opacity: showLoading ? 1 : 0,
									transition: 'opacity 0.4s ease',
									pointerEvents: showLoading ? 'all' : 'none',
								}}
								onTransitionEnd={() => {
									if (!showLoading) setLoadingVisible(false);
								}}
							>
								<Lottie loop animationData={AnimationFile} style={{ width: 180 }} />
								<Text fontSize="3xl" fontWeight="600" color="blue.500">
									{converting
										? 'Converting…'
										: loadProgress
											? loadProgress.percent != null
												? `${loadProgress.percent}%`
												: `${(loadProgress.loaded / 1024).toFixed(0)} KB`
											: ''}
								</Text>
							</Flex>
						)}
					</Box>
				</ModalContent>
			</Modal>

			<SignaturePickerModal
				isOpen={pickerOpen}
				onClose={() => setPickerOpen(false)}
				pickerDate={pickerDate}
				onDateChange={setPickerDate}
				onSelect={insertSignature}
				inserting={pickerInserting}
				t={t}
				signatures={SIGNATURES}
				showLetterheadOption={showPreview}
				withLetterhead={pickerWithLetterhead}
				onWithLetterheadChange={setPickerWithLetterhead}
				showPreview={showPreview}
			/>
		</>
	);
}

export default PDFSigner;
